/**
 * The GUI step builder for Recipe Builder's "New recipe" flow — no YAML
 * knowledge, no selectors, required. A recipe author picks "Add step",
 * chooses a plain-language step type, fills in the one or two fields that
 * type needs, and repeats. "Validate" and "Save" then talk to the exact same
 * `/api/recipes/validate` and `/api/recipes` routes the raw-YAML path always
 * used — this is a second way to PRODUCE the YAML, never a second way to run
 * it.
 *
 * The actual compile/reverse-map/YAML-writer logic lives in
 * `recipeStepBuilderLogic.ts` — split out because it is React-free and this
 * file is not; a self-check that only needs the logic must not have to
 * resolve 'react' to import it (CI runs self-checks from the repo root,
 * where 'react' — a dependency of `console/web` only — is not resolvable).
 */
import { useState } from 'react';
import {
  STEP_TYPE_LABELS,
  blankGuiStep,
  type GuiStep,
  type GuiStepKind,
} from './recipeStepBuilderLogic';

export {
  STEP_TYPE_LABELS,
  blankGuiStep,
  newGuiStepId,
  compileGuiSteps,
  recipeStepsToYaml,
  reverseMapRecipeSteps,
  type GuiStep,
  type GuiStepKind,
  type GuiStepClick,
  type GuiStepType,
  type GuiStepGoto,
  type GuiStepSeeText,
  type GuiStepWait,
  type GuiStepRetry,
  type GuiStepScreenshot,
  type CompiledGuiSteps,
} from './recipeStepBuilderLogic';

// ─────────────────────────────────────────────────────────────────────────────
// The component
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  application: string;
  rows: GuiStep[];
  onChange: (rows: GuiStep[]) => void;
  /** Server-reported validation errors, already resolved to a step index by the caller — `null` = not validated yet. */
  rowErrors: Map<string, string> | null;
  generalErrors: string[];
}

export function RecipeStepBuilder({ application: _application, rows, onChange, rowErrors, generalErrors }: Props) {
  const [pendingType, setPendingType] = useState<GuiStepKind>('click');

  const update = (id: string, patch: Partial<GuiStep>) => {
    onChange(rows.map((r) => (r.id === id ? ({ ...r, ...patch } as GuiStep) : r)));
  };
  const remove = (id: string) => onChange(rows.filter((r) => r.id !== id));
  const move = (id: string, dir: -1 | 1) => {
    const i = rows.findIndex((r) => r.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const addStep = () => onChange([...rows, blankGuiStep(pendingType)]);

  return (
    <div className="flex flex-col gap-3">
      {generalErrors.length > 0 && (
        <div className="rounded-lg border p-3 text-sm" style={{ borderColor: 'hsl(var(--danger))', color: 'hsl(var(--danger))' }}>
          <ul className="list-disc pl-5">{generalErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {rows.length === 0 && (
        <p className="text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
          No steps yet — pick a step type below and click "Add step".
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {rows.map((row, i) => {
          const error = rowErrors?.get(row.id);
          return (
            <li
              key={row.id}
              className="rounded-lg border p-3"
              style={{ borderColor: error ? 'hsl(var(--danger))' : 'hsl(var(--border))' }}
              data-testid={`gui-step-${i}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">
                  {i + 1}. {STEP_TYPE_LABELS[row.kind]}
                  {rowErrors && (error ? ' ❌' : ' ✅')}
                </span>
                <div className="flex gap-1">
                  <button type="button" className="btn btn-ghost text-xs" onClick={() => move(row.id, -1)} disabled={i === 0}>↑</button>
                  <button type="button" className="btn btn-ghost text-xs" onClick={() => move(row.id, 1)} disabled={i === rows.length - 1}>↓</button>
                  <button type="button" className="btn btn-ghost text-xs" style={{ color: 'hsl(var(--danger))' }} onClick={() => remove(row.id)}>Remove</button>
                </div>
              </div>

              <div className="mt-2 flex flex-col gap-2">
                {row.kind === 'click' && (
                  <>
                    <LabeledField label="Label (e.g. &quot;Create Course&quot;)">
                      <input className="field" value={row.label} onChange={(e) => update(row.id, { label: e.target.value })} data-testid={`gui-step-${i}-label`} />
                    </LabeledField>
                    <LabeledField label="Test ID (optional, for targeting a specific element — advanced use only, leave blank)">
                      <input className="field" value={row.testId} onChange={(e) => update(row.id, { testId: e.target.value })} data-testid={`gui-step-${i}-testid`} />
                    </LabeledField>
                  </>
                )}
                {row.kind === 'type' && (
                  <>
                    <LabeledField label="Field (the label on screen, e.g. &quot;Email address&quot;)">
                      <input className="field" value={row.field} onChange={(e) => update(row.id, { field: e.target.value })} data-testid={`gui-step-${i}-field`} />
                    </LabeledField>
                    <LabeledField label="Selector (optional, for advanced use only, leave blank)">
                      <input className="field" value={row.selector} onChange={(e) => update(row.id, { selector: e.target.value })} data-testid={`gui-step-${i}-selector`} />
                    </LabeledField>
                    <LabeledField label="Value (what to type)">
                      <input className="field" value={row.value} onChange={(e) => update(row.id, { value: e.target.value })} data-testid={`gui-step-${i}-value`} />
                    </LabeledField>
                  </>
                )}
                {row.kind === 'goto' && (
                  <LabeledField label="URL path (e.g. /courses — no full URL)">
                    <input className="field" value={row.path} onChange={(e) => update(row.id, { path: e.target.value })} data-testid={`gui-step-${i}-path`} />
                  </LabeledField>
                )}
                {row.kind === 'seeText' && (
                  <LabeledField label="Text to verify (e.g. &quot;Courses&quot; or &quot;Welcome back&quot;)">
                    <input className="field" value={row.text} onChange={(e) => update(row.id, { text: e.target.value })} data-testid={`gui-step-${i}-text`} />
                  </LabeledField>
                )}
                {row.kind === 'wait' && (
                  <LabeledField label="Duration (milliseconds) — leave it unless you know why you need more">
                    <input
                      className="field"
                      type="number"
                      value={row.durationMs}
                      onChange={(e) => update(row.id, { durationMs: Number(e.target.value) || 0 })}
                      data-testid={`gui-step-${i}-duration`}
                    />
                  </LabeledField>
                )}
                {row.kind === 'retry' && (
                  <LabeledField label="Count (how many retries of the step above — leave it at 1)">
                    <input
                      className="field"
                      type="number"
                      min={1}
                      value={row.count}
                      onChange={(e) => update(row.id, { count: Math.max(1, Number(e.target.value) || 1) })}
                      data-testid={`gui-step-${i}-count`}
                    />
                  </LabeledField>
                )}
                {row.kind === 'screenshot' && (
                  <p className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                    Turns on a screenshot comparison for the step above — no fields to fill in.
                  </p>
                )}
              </div>

              {error && <p className="mt-2 text-xs" style={{ color: 'hsl(var(--danger))' }}>{error}</p>}
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2">
        <select
          className="field w-auto"
          value={pendingType}
          onChange={(e) => setPendingType(e.target.value as GuiStepKind)}
          data-testid="gui-step-type-select"
        >
          {Object.entries(STEP_TYPE_LABELS).map(([kind, label]) => (
            <option key={kind} value={kind}>{label}</option>
          ))}
        </select>
        <button type="button" className="btn text-sm" onClick={addStep} data-testid="gui-add-step">
          + Add step
        </button>
      </div>
    </div>
  );
}

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span style={{ color: 'hsl(var(--text-muted))' }}>{label}</span>
      {children}
    </label>
  );
}
